import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM } from "./MediaAnnotationStudioConstraint";
import { measureAnnotationInspectorContent } from "./measureAnnotationInspectorContent";
import type { AnnotationFrameDraft, AnnotationFrameSummary } from "./types";

export type CapturedFramePresentationFrame = AnnotationFrameDraft | AnnotationFrameSummary;

export interface CapturedFrameEntry {
  readonly frame: CapturedFramePresentationFrame;
}

const DEFAULT_PAGE_SIZE = 3;
const FIT_EPSILON_PX = 0.5;

/** 슬롯 도구에 우선 높이를 배정하고 남는 공간으로 프레임 페이지 용량을 계산한다.
 * 마지막 페이지의 적은 행 수가 page-size를 다시 줄이는 순환을 막는다. */
function measureListCapacity(viewport: HTMLElement): number {
  const section = viewport.closest<HTMLElement>("[data-captured-frame-section]");
  const inspector = section?.parentElement?.querySelector<HTMLElement>(
    "[data-annotation-inspector]",
  );
  const viewportHeight = viewport.getBoundingClientRect().height;
  if (!section || !inspector) return viewportHeight;
  const sectionHeight = section.getBoundingClientRect().height;
  const sharedHeight = sectionHeight + inspector.getBoundingClientRect().height;
  const sectionMin = Number.parseFloat(getComputedStyle(section).minHeight) || 0;
  const preferredInspectorHeight = measureAnnotationInspectorContent(inspector);
  const inlinePreview = inspector.querySelector<HTMLElement>("[data-preview-layout='inline']");
  const previewHeight = inlinePreview
    ? // 늘어난 preview 높이를 되먹이면 페이지 행 수가 줄어드는 순환이 생긴다. 최소치만 예약한다.
      ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM *
        (Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16) +
      (Number.parseFloat(getComputedStyle(inspector).rowGap) || 0)
    : 0;
  const nominalHeight = Math.max(
    sectionMin,
    sharedHeight - preferredInspectorHeight - previewHeight,
  );
  return Math.max(0, nominalHeight - (sectionHeight - viewportHeight));
}

function measurePageSize(viewport: HTMLElement, list: HTMLUListElement): number | null {
  if (viewport.hidden) return null;

  const rows = Array.from(list.querySelectorAll<HTMLElement>("[data-captured-frame-row]"));
  const viewportHeight = measureListCapacity(viewport);
  if (rows.length === 0 || viewportHeight <= 0) return null;

  const rowHeights = rows.map((row) => row.getBoundingClientRect().height);
  const rowHeight = Math.max(...rowHeights);
  if (rowHeight <= 0) return null;

  const renderedRowsHeight = rowHeights.reduce((sum, height) => sum + height, 0);
  const listChromeHeight = Math.max(0, list.getBoundingClientRect().height - renderedRowsHeight);
  const availableRowsHeight = Math.max(0, viewportHeight - listChromeHeight);

  return Math.max(1, Math.floor((availableRowsHeight + FIT_EPSILON_PX) / rowHeight));
}

export function useCapturedFramePresentation({
  collectionId,
  capturedFrames,
  displayedFrames = capturedFrames,
  allCapturedFrameIds,
  activeFrameId,
  toolsLayoutKey,
}: {
  collectionId: string;
  /** selection·preview identity source. May span cameras even when displayedFrames does not. */
  capturedFrames: readonly CapturedFrameEntry[];
  /** 현재 필터가 적용된 pagination/list source. selection·preview 유효성은 capturedFrames를 따른다. */
  displayedFrames?: readonly CapturedFrameEntry[];
  allCapturedFrameIds: readonly string[];
  activeFrameId: string | null;
  /** 도구 모양/context가 바뀌면 새 자연 높이로 목록 용량을 다시 계산한다. */
  toolsLayoutKey?: string;
}) {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(activeFrameId);
  const [previewFrameId, setPreviewFrameIdState] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const listViewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const previousPageSizeRef = useRef(pageSize);
  const previousFrameIdsRef = useRef<{
    ids: readonly string[];
    set: ReadonlySet<string>;
  } | null>(null);
  const previousCollectionIdRef = useRef(collectionId);
  const capturedFrameById = useMemo(
    () => new Map(capturedFrames.map(({ frame }) => [frame.frameId, frame])),
    [capturedFrames],
  );
  const capturedFrameIds = useMemo(
    () => capturedFrames.map(({ frame }) => frame.frameId),
    [capturedFrames],
  );
  const capturedFrameIdSet = useMemo(() => new Set(capturedFrameIds), [capturedFrameIds]);
  const displayedFrameIds = useMemo(
    () => displayedFrames.map(({ frame }) => frame.frameId),
    [displayedFrames],
  );
  const displayedFrameIndexById = useMemo(
    () => new Map(displayedFrameIds.map((frameId, index) => [frameId, index])),
    [displayedFrameIds],
  );
  const previewFrame = previewFrameId ? capturedFrameById.get(previewFrameId) : undefined;
  const selectedFrame = selectedFrameId ? capturedFrameById.get(selectedFrameId) : undefined;
  const pageCount = Math.max(1, Math.ceil(displayedFrames.length / pageSize));
  const visibleFrames = displayedFrames.slice((page - 1) * pageSize, page * pageSize);

  /** 페이지 크기는 목록 viewport 의 실제 높이에서 나온다. */
  useLayoutEffect(() => {
    const viewport = listViewportRef.current;
    const list = listRef.current;
    if (!viewport || !list) return;

    const updatePageSize = () => {
      const measuredPageSize = measurePageSize(viewport, list);
      if (measuredPageSize === null) return;
      setPageSize((current) => (current === measuredPageSize ? current : measuredPageSize));
    };

    updatePageSize();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updatePageSize);
    observer.observe(viewport);
    observer.observe(list);
    const inspector = viewport
      .closest("[data-captured-frame-section]")
      ?.parentElement?.querySelector<HTMLElement>("[data-annotation-inspector]");
    if (inspector) observer.observe(inspector);
    const toolsContent = inspector?.querySelector(
      "[data-annotation-tools-content], [data-annotation-placeholder-content]",
    );
    if (toolsContent) observer.observe(toolsContent);
    // inline 미리보기 추가/제거는 Inspector 외곽 높이를 바꾸지 않을 수 있다.
    const mutationObserver = inspector ? new MutationObserver(updatePageSize) : null;
    if (inspector) mutationObserver?.observe(inspector, { childList: true });
    const firstRow = list.querySelector<HTMLElement>("[data-captured-frame-row]");
    if (firstRow) observer.observe(firstRow);

    return () => {
      observer.disconnect();
      mutationObserver?.disconnect();
    };
  }, [displayedFrames.length, toolsLayoutKey]);

  useEffect(() => {
    const collectionChanged = previousCollectionIdRef.current !== collectionId;
    const previousFrameIds = collectionChanged ? null : previousFrameIdsRef.current;
    const nextFrameIds =
      previousFrameIds?.ids === allCapturedFrameIds
        ? previousFrameIds
        : { ids: allCapturedFrameIds, set: new Set(allCapturedFrameIds) };
    const addedFrameIds =
      previousFrameIds === null
        ? []
        : previousFrameIds.ids === allCapturedFrameIds
          ? []
          : allCapturedFrameIds.filter((frameId) => !previousFrameIds.set.has(frameId));
    const initialHistory = previousFrameIds === null;

    if (
      (collectionChanged && previewFrameId !== null) ||
      (previewFrameId !== null && !capturedFrameIdSet.has(previewFrameId))
    ) {
      setPreviewFrameIdState(null);
    }

    const fallbackSelectedFrameId =
      activeFrameId !== null && capturedFrameIdSet.has(activeFrameId) ? activeFrameId : null;
    if (
      collectionChanged ||
      (selectedFrameId !== null && !capturedFrameIdSet.has(selectedFrameId))
    ) {
      setSelectedFrameId(fallbackSelectedFrameId);
    }

    let latestAddedFrameId =
      activeFrameId !== null && addedFrameIds.includes(activeFrameId) ? activeFrameId : undefined;
    for (
      let index = addedFrameIds.length - 1;
      latestAddedFrameId === undefined && index >= 0;
      index -= 1
    ) {
      const frameId = addedFrameIds[index];
      if (frameId !== undefined && capturedFrameIdSet.has(frameId)) latestAddedFrameId = frameId;
    }
    if (latestAddedFrameId) {
      setSelectedFrameId(latestAddedFrameId);
    }

    const nextPageCount = Math.max(1, Math.ceil(displayedFrameIds.length / pageSize));
    const revealFrameIndex = latestAddedFrameId
      ? (displayedFrameIndexById.get(latestAddedFrameId) ?? -1)
      : initialHistory
        ? displayedFrameIds.length - 1
        : -1;
    const previousPageSize = previousPageSizeRef.current;
    const pageSizeChanged = previousPageSize !== pageSize;
    const selectedFrameIndex = selectedFrameId
      ? (displayedFrameIndexById.get(selectedFrameId) ?? -1)
      : -1;
    const activeFrameIndex = activeFrameId
      ? (displayedFrameIndexById.get(activeFrameId) ?? -1)
      : -1;
    setPage((current) => {
      if (revealFrameIndex >= 0) return Math.floor(revealFrameIndex / pageSize) + 1;
      if (pageSizeChanged && displayedFrameIds.length > 0) {
        const previousPageStart = (current - 1) * previousPageSize;
        const previousPageEnd = previousPageStart + previousPageSize;
        const selectedFrameWasVisible =
          selectedFrameIndex >= previousPageStart && selectedFrameIndex < previousPageEnd;
        const activeFrameWasVisible =
          activeFrameIndex >= previousPageStart && activeFrameIndex < previousPageEnd;
        const anchorIndex = selectedFrameWasVisible
          ? selectedFrameIndex
          : activeFrameWasVisible
            ? activeFrameIndex
            : Math.min(previousPageStart, displayedFrameIds.length - 1);
        return Math.min(Math.floor(anchorIndex / pageSize) + 1, nextPageCount);
      }
      return Math.min(Math.max(current, 1), nextPageCount);
    });

    previousPageSizeRef.current = pageSize;
    previousCollectionIdRef.current = collectionId;
    previousFrameIdsRef.current = nextFrameIds;
  }, [
    activeFrameId,
    allCapturedFrameIds,
    capturedFrameIdSet,
    collectionId,
    displayedFrameIds,
    displayedFrameIndexById,
    pageSize,
    previewFrameId,
    selectedFrameId,
  ]);

  const revealFrame = useCallback(
    (frameId: string) => {
      const frameIndex = displayedFrameIndexById.get(frameId) ?? -1;
      if (frameIndex >= 0) {
        setPage(Math.floor(frameIndex / pageSize) + 1);
        return true;
      }
      return capturedFrameIdSet.has(frameId);
    },
    [capturedFrameIdSet, displayedFrameIndexById, pageSize],
  );

  const selectPreview = useCallback(
    (frameId: string) => {
      if (!revealFrame(frameId)) return;
      setPreviewFrameIdState(frameId);
    },
    [revealFrame],
  );

  const setPreviewFrameId = useCallback(
    (frameId: string | null) => {
      if (frameId === null) {
        setPreviewFrameIdState(null);
        return;
      }
      selectPreview(frameId);
    },
    [selectPreview],
  );

  const togglePreview = useCallback(
    (frameId: string) => {
      if (previewFrameId === frameId) {
        setPreviewFrameId(null);
        return;
      }
      selectPreview(frameId);
    },
    [previewFrameId, selectPreview, setPreviewFrameId],
  );

  return {
    page,
    pageCount,
    visibleFrames,
    listViewportRef,
    listRef,
    selectedFrameId,
    selectedFrame,
    setSelectedFrameId,
    previewFrameId,
    previewFrame,
    setPreviewFrameId,
    revealFrame,
    selectPreview,
    togglePreview,
    previousPage: () => setPage((current) => Math.max(1, current - 1)),
    nextPage: () => setPage((current) => Math.min(pageCount, current + 1)),
  };
}
